import { useState, type ReactNode } from 'react'
import { useLocale } from '@/i18n/locale'

/**
 * Инлайн-термин системы с определением по наведению (мандат Тони 2026-08-01:
 * словарь не секцией, а тултипами — «справа над словом кружочек со знаком
 * вопроса, пользователь наводит — получает видимое определение»).
 *
 * Бейдж «?» виден всегда (superscript-кружок), определение — по hover/focus
 * на десктопе и по тапу на мобильном. Работает после гидрации; в статике
 * бейдж есть, тултип скрыт — на SEO не влияет (термины вспомогательные).
 */

export type TermKey =
  | 'ecosystem'
  | 'perimeter'
  | 'memory-layers'
  | 'anchor-pilot'
  | 'unpacking'
  | 'verification'
  | 'acceptance'
  | 'keeper'
  | 'auditor'
  | 'club'

const TERMS: Record<TermKey, { ru: string; en: string }> = {
  ecosystem: {
    ru: 'ИИ-экосистема — одна система на всю компанию: люди, данные и ИИ-помощники работают вместе, а вы видите всё в одном окне.',
    en: 'AI ecosystem — one system for the whole company: people, data and AI assistants work together, and you see everything in one window.',
  },
  perimeter: {
    ru: 'Контур владения — архитектура, при которой всё принадлежит компании, а не людям.',
    en: 'Ownership perimeter — an architecture where everything belongs to the company, not to people.',
  },
  'memory-layers': {
    ru: 'Слои памяти — иерархия инструкций: личное → командное → организация. ИИ помнит компанию, а не чат.',
    en: 'Memory layers — a hierarchy of instructions: personal → team → organization. The AI remembers the company, not a chat.',
  },
  'anchor-pilot': {
    ru: 'Якорный пилот — одна боль владельца, доведённая до достоверного результата. Создаёт доверие ко всей системе.',
    en: 'Anchor pilot — one owner’s pain driven to a trustworthy result. It creates trust in the whole system.',
  },
  unpacking: {
    ru: 'Распаковка роли — интервью-процесс: зона ответственности → процессы → решения → KPI → в память AI.',
    en: 'Role unpacking — an interview process: responsibility zone → processes → decisions → KPIs → into AI memory.',
  },
  verification: {
    ru: 'Верификация 5/5 — ворота этапа: роль принята, только если ИИ отвечает на контрольные вопросы на 5 из 5.',
    en: 'Verification 5/5 — the stage gate: a role is accepted only if the AI answers control questions 5 out of 5.',
  },
  acceptance: {
    ru: 'Артефактная приёмка — оплата за проверяемые результаты, не за часы и обещания.',
    en: 'Artifact acceptance — payment for verifiable results, not for hours or promises.',
  },
  keeper: {
    ru: 'Хранитель — человек в вашей команде, который ведёт систему после нас.',
    en: 'The Keeper — a person on your team who runs the system after us.',
  },
  club: {
    ru: 'Клуб On vAIbe — закрытый чат в Telegram: только собственники компаний с оборотом от 100 млн ₽, которые уже внедряют и работают в AI каждый день. Отдельно — 1 800 $ в год; клиентам внедрения входит в стоимость продукта.',
    en: 'The On vAIbe club — a closed Telegram chat: owners of companies with revenue from $1M who already run AI in their daily work. Standalone it is $1,800 a year; for implementation clients it is included in the product price.',
  },
  auditor: {
    ru: 'Аудитор — независимый ИИ-прогон, который вскрывает реальные статусы: «у меня всё готово» ≠ факт.',
    en: 'The Auditor — an independent AI pass that uncovers real statuses: “it’s all done” ≠ fact.',
  },
}

/**
 * plain-режим — для мест внутри интерактивных элементов (<summary> и т.п.):
 * без <button> (a11y nested-interactive), тултип по CSS group-hover.
 */
export function Term({ k, children, plain = false }: { k: TermKey; children: ReactNode; plain?: boolean }) {
  const locale = useLocale()
  const [open, setOpen] = useState(false)
  const def = TERMS[k][locale]
  if (plain) {
    return (
      <span className="relative inline group/term">
        {children}
        <span
          aria-hidden
          className="inline-flex items-center justify-center align-super ml-0.5 w-3.5 h-3.5 rounded-full border border-accent/60 text-accent text-[9px] leading-none font-medium cursor-help select-none"
        >
          ?
          <span className="hidden group-hover/term:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 max-w-[80vw] rounded-card bg-ink text-bg text-small font-normal text-left normal-case tracking-normal leading-snug p-3 shadow-lg z-50 pointer-events-none">
            {def}
          </span>
        </span>
      </span>
    )
  }
  return (
    <span className="relative inline">
      {children}
      <button
        type="button"
        aria-label={def}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={(e) => {
          e.preventDefault()
          setOpen((v) => !v)
        }}
        className="relative inline-flex items-center justify-center align-super ml-0.5 w-3.5 h-3.5 rounded-full border border-accent/60 text-accent text-[9px] leading-none font-medium cursor-help select-none hover:bg-accent hover:text-bg transition-colors"
      >
        ?
        {open && (
          <span
            role="tooltip"
            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 max-w-[80vw] rounded-card bg-ink text-bg text-small font-normal text-left normal-case tracking-normal leading-snug p-3 shadow-lg z-50 pointer-events-none"
          >
            {def}
          </span>
        )}
      </button>
    </span>
  )
}
